import { makeAutoObservable, runInAction } from 'mobx'
import type { ButtonGroupButton } from './types'

/**
 * ButtonGroupStore - MobX store для управління кнопками у ButtonGroup
 * Відповідає за розподіл кнопок між основною зоною та dropdown меню
 */
export class ButtonGroupStore {
    /** Оригінальний масив кнопок з усіма налаштуваннями */
    originalButtons: ButtonGroupButton[] = []

    /** Кнопки, що відображаються у основній зоні */
    buttons: ButtonGroupButton[] = []

    /** Кнопки, що відображаються у dropdown меню */
    dropdownButtons: ButtonGroupButton[] = []

    constructor() {
        makeAutoObservable(this)
    }

    /**
     * Встановлює оригінальний список кнопок
     * Додає ключ та дефолтні значення для priority і sort
     */
    setOriginalButtons(buttons: Record<string, ButtonGroupButton>): void {
        if (typeof buttons === 'object' && buttons !== null) {
            const buttonsArray: ButtonGroupButton[] = []

            Object.keys(buttons).forEach((key) => {
                const button = buttons[key]
                if (!button) return

                buttonsArray.push({
                    ...button,
                    key: button.key || key,
                    priority: button.priority || ('button' as const),
                    sort: button.sort ?? 100,
                })
            })

            this.originalButtons = buttonsArray.sort((a, b) => (a.sort ?? 100) - (b.sort ?? 100))
            this.updateButtons()
        }
    }

    /**
     * Оновлює розподіл кнопок між основною зоною та dropdown
     * Використовує оригінальний розподіл на основі priority
     */
    updateButtons(): void {
        // Видаляємо службові поля для рендерингу
        const originalButtons = this.originalButtons.map(({ depFn, depStore, sort, ...button }) => button)
        this.buttons = originalButtons.filter((btn) => btn.priority === 'button')
        this.dropdownButtons = originalButtons.filter((btn) => btn.priority === 'dropdown')
    }

    /**
     * Адаптивний розподіл кнопок на основі доступного простору
     * @param visibleCount - кількість кнопок, які можна відобразити
     */
    adjustButtons(visibleCount: number): void {
        runInAction(() => {
            const primaryButtons = this.originalButtons.filter((btn) => btn.priority === 'button')
            const dropdownOnlyButtons = this.originalButtons.filter((btn) => btn.priority === 'dropdown')

            if (visibleCount >= primaryButtons.length) {
                // Вистачає місця для всіх primary кнопок
                this.buttons = primaryButtons.map(({ depFn, depStore, sort, ...button }) => button)
                this.dropdownButtons = dropdownOnlyButtons.map(({ depFn, depStore, sort, ...button }) => button)
            } else {
                // Частина primary кнопок йде в dropdown
                this.buttons = primaryButtons
                    .slice(0, visibleCount)
                    .map(({ depFn, depStore, sort, ...button }) => button)
                this.dropdownButtons = [
                    ...primaryButtons.slice(visibleCount).map(({ depFn, depStore, sort, ...button }) => button),
                    ...dropdownOnlyButtons.map(({ depFn, depStore, sort, ...button }) => button),
                ]
            }
        })
    }
}
